import { type San, type Uci, type Score } from '@chess/api-model';
import { scoreForWhite } from '@chess/api-analysis';
import { MOVE_GRADE_LABEL, type GameScan, type MoveGrade } from '@chess/api-review';

/** What the sheet knows about a move from the scan: its grade and the score change it caused. */
export interface SheetGrade {
  /** The app's grade (`GameScan.grade`): the user's rules decide blunder and miss, as on the Blunders and Misses screens. */
  grade: MoveGrade;
  /** Centipawns given up against the best line, never negative. */
  delta: number;
  /** The score before the move, from White's side; absent when the scan lacks the position. */
  before?: Score;
  /** The score after the move, from White's side; absent when the scan lacks the position. */
  after?: Score;
}

/** The grade of each move, keyed by its index in the move list. */
export type Judgements = ReadonlyMap<number, SheetGrade>;

/** What the sheet needs of a move to judge it against the scan. */
export interface SheetMove {
  san: San;
  uci: Uci;
}

/**
 * The grade of every move the scan can judge so far, through the review's
 * `GameScan` (which carries the user's rules). A complete scan carries its
 * own verdicts; a partial one is read position by position, so a move is
 * graded as soon as the positions before and after it are scanned, and the
 * sheet fills in while the scan runs.
 */
export function judgementsOf(scan: GameScan | undefined, moves: readonly SheetMove[]): Judgements {
  const map = new Map<number, SheetGrade>();
  if (!scan) return map;
  for (let i = 0; i < moves.length; i++) {
    const ply = i + 1;
    const judgement = scan.judgement(ply, moves[i]);
    const grade = scan.grade(ply, moves[i], ply % 2 ? 'white' : 'black');
    if (!judgement || !grade) continue;
    const before = scan.position(i);
    const after = scan.position(ply);
    map.set(i, { grade, delta: judgement.delta, before: before && scoreForWhite(before), after: after && scoreForWhite(after) });
  }
  return map;
}

/** A White-relative score as the score bar writes it: +0.30, -1.20, M4, -M2. */
export function scoreText(score: Score): string {
  if (score.mate !== undefined) return score.mate >= 0 ? `M${score.mate}` : `-M${Math.abs(score.mate)}`;
  const value = (score.cp ?? 0) / 100;
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
}

/**
 * The tooltip of a graded move: its grade, the score before and after from
 * White's side, and what the move gave up when it gave up anything.
 * "Inaccuracy · +0.30 → +0.18 · gave up 0.12".
 */
export function tooltipOf(grade: SheetGrade): string {
  const parts = [MOVE_GRADE_LABEL[grade.grade]];
  if (grade.before && grade.after) parts.push(`${scoreText(grade.before)} → ${scoreText(grade.after)}`);
  else if (grade.after) parts.push(`now ${scoreText(grade.after)}`);
  if (grade.delta > 0) parts.push(`gave up ${(grade.delta / 100).toFixed(2)}`);
  return parts.join(' · ');
}
